import { format, parseISO } from 'date-fns'
import { useTranslation } from 'libs/i18n'

import { HistoryTableProps } from './types'
import { CardWrapper, EmptyTable } from 'shared/components'

type ChangeMessage = {
	added?: { name?: string; object?: string }
	changed?: { name?: string; fields?: string[] | string; object?: string }
}

const parseChanges = (message?: string): ChangeMessage[] => {
	if (!message) return []

	try {
		const parsed = JSON.parse(message)

		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

const joinFields = (fields?: string[] | string) =>
	Array.isArray(fields) ? fields.join(', ') : (fields ?? '')

export const HistoryTable = ({ results, isLoading }: HistoryTableProps<any>) => {
	const { t } = useTranslation()
	const hasResults = Array.isArray(results) && results.length > 0

	return (
		<div className="px-5 lg:px-0">
			<CardWrapper className="overflow-hidden">
				{hasResults ? (
					<div className="overflow-x-auto">
						<table className="w-full text-left text-[13px] text-foreground">
							<thead>
								<tr className="border-b border-border bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
									<th className="w-44 px-4 py-3 font-semibold">{t('Date/time')}</th>
									<th className="w-60 px-4 py-3 font-semibold">{t('User')}</th>
									<th className="px-4 py-3 font-semibold">{t('Action')}</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{results.map((item) => (
									<tr key={item.id} className="align-top">
										<td className="whitespace-nowrap px-4 py-3 tabular-nums text-muted-foreground">
											{format(parseISO(item.action_time), 'dd.MM.yyyy HH:mm')}
										</td>
										<td className="px-4 py-3 font-medium">{item.user?.email ?? '—'}</td>
										<td className="px-4 py-3">
											{parseChanges(item.change_message).map((message, index) => {
												if (message.added) {
													return (
														<p key={index}>
															{t('Added')} {message.added.name}
															{message.added.object ? ` "${message.added.object}"` : ''}
														</p>
													)
												}

												if (message.changed) {
													return (
														<p key={index}>
															{t('Changed')} {message.changed.name} &quot;
															{joinFields(message.changed.fields)}&quot;
															{message.changed.object ? ` – ${message.changed.object}` : ''}
														</p>
													)
												}

												return null
											})}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<EmptyTable isLoading={isLoading} />
				)}
			</CardWrapper>
		</div>
	)
}
